/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBlockPartnerListQuery,
  EntWebsitePartnerListFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import Button from 'bundles/ent-website/contentful/core/ContentfulButton';
import Image from 'bundles/ent-website/contentful/core/Image';
import { entWebsiteBlockPartnerListQuery } from 'bundles/ent-website/queries/EntWebsitePartnerList';
import parseLayoutOptions from 'bundles/ent-website/utils/parseLayoutOptions';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsitePartnerListFragment>;

export function PartnerListModule({ content, assembly }: Props) {
  const { partnersCollection, tagline, description, ctaTarget, ctaText } = content;
  const options = parseLayoutOptions(assembly.layoutOptions);
  const theme = useTheme();

  const styles = {
    tagline: css`
      margin-bottom: var(--cds-spacing-600);
      ${breakpoints.down('xs')} {
        line-height: 40px !important;
      }

      line-height: 100% !important;
    `,

    taglineEmbedded: css`
      font-size: 2rem !important;
    `,

    description: css`
      margin-top: var(--cds-spacing-300);
      font-size: 20px !important;
      line-height: 150% !important;
      letter-spacing: -0.02em;

      ${breakpoints.down('xs')} {
        font-size: 16px !important;
        letter-spacing: -0.03em;
        margin-bottom: var(--cds-spacing-300);
      }
    `,
    ctaDescription: css`
      margin-top: var(--cds-spacing-400);
      ${breakpoints.down('xs')} {
        margin-bottom: var(--cds-spacing-600);
      }
    `,
    partnerList: css`
      overflow: hidden;
      ${options.innerLayout && `max-width: 406px;`}

      ul {
        list-style-type: none;
        position: relative;
        padding-inline-start: 0;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        margin-top: 12px;
        margin-bottom: 0;
        left: 1px;
        top: 1px;
      }

      ul li {
        overflow: hidden;
        height: 98px;
        display: flex;
        padding: 8px 24px;
        align-items: center;
        justify-content: center;
        border-right: 1px solid var(--cds-color-grey-50);
        border-bottom: 1px solid var(--cds-color-grey-50);

        ${breakpoints.down('xs')} {
          max-height: 55px;
          padding: 8px;
        }
      }

      ul li img {
        max-width: 100%;
        object-fit: cover;
        max-height: 100%;
        ${breakpoints.down('xs')} {
          max-height: 48px;
        }
      }
    `,
  };

  const imagePanel = () => (
    <div css={styles.partnerList}>
      {partnersCollection?.items && (
        <ul>
          {partnersCollection.items.map((partner) =>
            partner?.logo ? (
              <li key={partner.slug}>
                <Image content={partner.logo} width="auto" />
              </li>
            ) : null
          )}
        </ul>
      )}
    </div>
  );

  const contentPanel = () => (
    <div>
      <Typography variant="d2semibold" css={[styles.tagline, options.innerLayout ? styles.taglineEmbedded : null]}>
        {tagline}
      </Typography>

      {description && (
        <Typography2 component="p" variant="bodyPrimary" css={styles.description}>
          {description}
        </Typography2>
      )}

      {ctaText && ctaTarget && (
        <div css={styles.ctaDescription}>
          <Button variant="ghost" component="a" href={ctaTarget} position={0} module={content} icon={<ArrowNextIcon />}>
            {ctaText}
          </Button>
        </div>
      )}
    </div>
  );

  return <BlockLayout assembly={assembly} renderContent={contentPanel} renderImage={imagePanel} alignItems="center" />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockPartnerListModule = withBlockQuery<EntWebsitePartnerListFragment, EntWebsiteBlockPartnerListQuery>(
  entWebsiteBlockPartnerListQuery
)(PartnerListModule);
